/**
 * Content tickets: how a stored file is opened *by URL* (decision D5).
 *
 * Main's components open a file by URL - `<a :href target=_blank>`,
 * `window.open`, `<img :src>`, a PDF `<iframe>`, PDF.js - against keyon's
 * `file.php`, which keyon's session cookie authenticates. The port
 * authenticates by bearer token, which no URL can carry. So for each opening
 * the SPA asks the API, bearer in hand, for a ticket
 * (`POST /documents/{uid}/content-tickets`); the API makes the whole read
 * decision - who may see the file, its scan state, a recent second factor -
 * and answers with an API URL that reads that one file once, within a
 * minute. It is never a storage URL: the SPA never receives one.
 *
 * `resolveFileUrl()` turns main's file reference (`keyonFileUrl(uid)`) into
 * such a URL. `installStoredFileLinks()` does it for every anchor and
 * `window.open` in main's components without their templates changing; the
 * few that embed a file (`<img>`, `<iframe>`, PDF.js) call `resolveFileUrl()`
 * themselves, because an element's `src` is fetched the moment it is set.
 *
 * A refusal keeps main's states (keyonFiles.refusalStateFromResponse): 409 is
 * "Scanning for malware", 404/410 "Document unavailable", 401 `mfa_required`
 * a fresh sign-in, 429 rate limited.
 */

import api from './client';
import { isFileUid, parseFileUrl } from '@/utils/fileUrl';
import {
  downloadCredentialBundle,
  downloadKeyonFile,
  downloadRefusalText,
  refusalStateFromResponse,
} from '@/api/keyonFiles';
import { redirectToCodePage } from '@/api/mfaRequired';

/**
 * A ticket URL for one file, after the API's read decision.
 * @returns {Promise<{ok:true,url:string}|{ok:false,state:string,code:string|null}>}
 */
export async function issueContentTicket(uid, { download = false } = {}, deps = {}) {
  if (!isFileUid(uid)) return { ok: false, state: 'unavailable', code: null };
  const http = deps.http || api.raw;
  let res;
  try {
    // Every status comes back as a response, so a refusal is read here and
    // never reaches the client's sign-out-on-401 handling.
    res = await http.post(`/documents/${uid}/content-tickets`, download ? { download: true } : {}, {
      validateStatus: () => true,
    });
  } catch (_) {
    return { ok: false, state: 'error', code: null };
  }
  const body = (res && res.data) || {};
  if (res && res.status === 201 && body.data && typeof body.data.url === 'string') {
    return { ok: true, url: body.data.url };
  }
  const code = body.error && typeof body.error.code === 'string' ? body.error.code : null;
  return { ok: false, state: refusalStateFromResponse(res ? res.status : 0, code), code };
}

/**
 * The URL to fetch for a file reference: a ticket URL for `file.php?id=<uid>`
 * (or a bare uid); a `blob:` or `data:` URL - a file not yet sent - as it is.
 * @returns {Promise<{ok:true,url:string}|{ok:false,state:string,code:string|null}>}
 */
export async function resolveFileUrl(ref, { download, deps } = {}) {
  if (typeof ref === 'string' && /^(blob|data):/i.test(ref)) return { ok: true, url: ref };
  const parsed = isFileUid(ref) ? { kind: 'file', uid: ref, download: false } : parseFileUrl(ref);
  if (!parsed || parsed.kind !== 'file') return { ok: false, state: 'unavailable', code: null };
  return issueContentTicket(parsed.uid, { download: download === undefined ? parsed.download : download }, deps);
}

/**
 * Open a file reference in a new tab (or this one), as main's anchors and
 * window.open calls did. The tab is opened *before* the ticket is asked for,
 * while the click still counts as the user's, so no popup blocker stops it;
 * it is pointed at the ticket URL once there is one, with no `opener`. A
 * refusal is written into that tab in main's words - or, for a lapsed second
 * factor, the tab closes and the person signs in again.
 * @returns {Promise<{ok:boolean,state?:string}>}
 */
export async function openStoredFile(ref, { download, sameWindow = false, deps = {} } = {}) {
  const win = deps.window || (typeof window !== 'undefined' ? window : null);
  const open = deps.open || (win && win.__pacmnyOpen) || (win && win.open ? win.open.bind(win) : null);
  const tab = sameWindow || !open ? null : open('', '_blank');

  const resolved = await resolveFileUrl(ref, { download, deps });

  if (resolved.ok) {
    if (tab) {
      try { tab.opener = null; } catch (_) { /* cross-origin already */ }
      tab.location.replace(resolved.url);
    } else if (win) {
      win.location.assign(resolved.url);
    }
    return { ok: true };
  }

  if (resolved.state === 'mfa_required') {
    if (tab && typeof tab.close === 'function') tab.close();
    (deps.onMfaRequired || (() => redirectToCodePage()))();
    return { ok: false, state: resolved.state };
  }

  const text = downloadRefusalText(resolved.state);
  if (tab && tab.document) {
    tab.document.title = text || '';
    if (tab.document.body) tab.document.body.textContent = text || '';
  } else if (text && win && typeof win.alert === 'function') {
    win.alert(text);
  }
  return { ok: false, state: resolved.state };
}

let installedOn = null;

/**
 * Route every file reference main's components open by URL through a ticket:
 *
 *  - a click on an `<a href="…/file.php?id=…">` (NurseCard, AidCard,
 *    UserProfile, the Documents page, AddUserForm's "View PDF", the directive
 *    viewer's "Open in new tab"): the new tab gets a ticket URL; an anchor
 *    with a `download` attribute is saved instead, through the bridged GET;
 *    a bundle link downloads credentials.zip;
 *  - `window.open(fileReference, …)` (the Documents page, message
 *    attachments): the same, with the window opened synchronously.
 *
 * Anything that is not a file reference passes through untouched. Installed
 * once, from main.js, before the app mounts.
 */
export function installStoredFileLinks(deps = {}) {
  const win = deps.window || (typeof window !== 'undefined' ? window : null);
  const doc = deps.document || (win ? win.document : null);
  if (!win || !doc || installedOn === doc) return;
  installedOn = doc;

  doc.addEventListener('click', (event) => {
    if (event.defaultPrevented || (event.button !== undefined && event.button !== 0)) return;
    const anchor = event.target && typeof event.target.closest === 'function' ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    const ref = parseFileUrl(href);
    if (!ref) return;

    event.preventDefault();

    if (ref.kind === 'bundle') {
      downloadCredentialBundle(ref.owner);
      return;
    }
    if (anchor.hasAttribute('download')) {
      downloadKeyonFile(ref.uid, anchor.getAttribute('download') || anchor.textContent.trim() || 'download');
      return;
    }
    const newTab = anchor.getAttribute('target') === '_blank' || event.ctrlKey || event.metaKey || event.shiftKey;
    openStoredFile(href, { sameWindow: !newTab, deps: { ...deps, window: win } });
  }, true);

  // The original, kept for openStoredFile's own tab and everything else.
  const original = win.open.bind(win);
  win.__pacmnyOpen = original;
  win.open = function openWithFileTickets(url, target, features) {
    const ref = typeof url === 'string' ? parseFileUrl(url) : null;
    if (!ref || ref.kind !== 'file') return original(url, target, features);
    openStoredFile(url, { deps: { ...deps, window: win, open: original } });
    // Main's callers pass noopener and so already expect no handle back.
    return null;
  };
}

export default { issueContentTicket, resolveFileUrl, openStoredFile, installStoredFileLinks };
